/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-misused-promises, @typescript-eslint/no-unsafe-type-assertion */
/*
 * Integrates TextResources through i18next into vuejs.
 *
 * You can use TRs in three different ways.
 * * use a `v-tr` attribute such as `<span v-tr="'My_Text_Resource'" />` (preferred).
 * * use `$tr` inside a Vue component or a template such as `const tr = this.$tr("key");`, i.e., inside reactive code.
 *   This returns "…" on the first invocation. Once the resource is ready, a rerender happens and the real text is
 *   returned by `$tr`.
 * * more low-level, call `I18Next.tr` to get a promise that resolves to the TextResource's value.
 *
 * Conditional Rendering (`v-tr.if`)
 * ---------------------------------
 * Use `<span v-tr.if="maybeKey" />` when the key may be `null` or `undefined`.
 * It is syntactic sugar for `v-if="x" v-tr="x"` and helps avoid mismatches such as
 * `v-if="x"` together with `v-tr="y"` by making both concerns use one value.
 * Behavior:
 * * with a key: behaves like `v-tr`,
 * * without a key (`null` / `undefined`): hides the element,
 * * without `.if`, missing keys are considered a usage error.
 *
 * Interpolation
 * -------------
 * The basics are explained at https://www.i18next.com/translation-function/interpolation#interpolation, however we
 * use `{this form}` for placeholders instead of `{{this form}}`.
 * To use interpolations:
 * * Do `<span v-tr="{key: 'My_Text_Resource', [an anchor]: 'replacement'}" />`,
 * * or use `$tr('My_Text_Resource', {[an anchor]: 'replacement})` from reactive code,
 * * or use the low-level `I18Next.tr('My_Text_Resource', {[an anchor]: 'replacement'})`.
 * It's safe to put arbitrary user provided content into interpolations as it gets escaped,
 * see https://www.i18next.com/translation-function/interpolation#unescape.
 * If you are really in for some trouble, a replacement of `{-trusted this form}` does not get escaped.
 * (The key has to start with `trusted` and there has to be the extra dash.) Note that you'll have to take the blame
 * for the resulting XSS attacks.
 *
 * Plurals, Grammar, …
 * -------------------
 * There are no automatic plurals; though some of this https://www.i18next.com/translation-function/plurals#plurals
 * might already work, we probably want to change to fluent format for this: https://github.com/i18next/i18next-fluent
 *
 * HTML
 * ----
 * Some HTML is supported in TextResources. Please see the sanitation code below for details.
 * Note that Vue.js removes HTML from `{{ $tr(...) }}` because it always does
 * so with `{{ }}` interpolations.
 *
 * Cultures
 * --------
 * Note that TextResources exist for several "cultures", US (default) and C (shows raw TextResource [keys],)
 * call window.MiaCulture() to see the raw keys.
 *
 * Reactivity
 * ----------
 * The reactive and watchEffect in this code are probably not how these
 * directives should be written anymore in Vue 3. While this seems to work, it
 * could be that there are some performance impacts due to excessive tracking
 * with this approach.
 * Also, the overly complicated singleton approach is not needed anymore since
 * i18next now has proper instances.
 *
 * Rant
 * ----
 * TextResources™ by miaplaza are a weird animal. We keep translation strings in the database
 * so that content developers can update them without a release. We also have a few tools to transfer text
 * resources between different portals (AI/CD/Miacademy.) See https://dev.miaplaza.com/miaplaza/website/issues/11272
 * for ideas on how to improve upon this.
 */

import { MiaPlaza } from "../../Reinforced.Typings";

import { reactive, watch } from "vue";
import type { App, DirectiveBinding } from "vue";
import axios from "axios";
import DOMPurify from "dompurify";
import i18next, { type PostProcessorModule } from "i18next";
import ChainedBackend from "i18next-chained-backend";
import LocalStorageBackend from "i18next-localstorage-backend";
import MultiloadAdapter from "i18next-multiload-backend-adapter";
import XHR from "i18next-http-backend";
import mapValues from "lodash-es/mapValues";
import Log from "../../utils/Log";
import { type I18NextStore as Store, useI18NextStore } from "./Stores/I18NextStore";
import type { HttpBackendOptions } from "i18next-http-backend";
import type { RequestCallback } from "i18next-http-backend";

const LOADING_PLACEHOLDER = "…";

/**
 * Modifiers accepted by the `v-tr` directive.
 *
 * `if` is syntactic sugar for `v-if="x" v-tr="x"`. It is handy when `x` is an
 * expression and prevents mismatches like `v-if="x"` with `v-tr="y"`.
 */
const _TR_DIRECTIVE_MODIFIERS = [
	"if",
] as const;

/**
 * Modifiers accepted by the `v-tr` directive.
 */
type TrDirectiveModifiers = (typeof _TR_DIRECTIVE_MODIFIERS)[number];

/**
 * Object form accepted by `v-tr`, where `key` is the TR key and all other
 * properties are interpolation values.
 */
type TrEncodedKeyAndInterpolations = { key: string, [interpolationKey: string]: unknown };

/**
 * Raw value accepted by `v-tr`: either just a key string or an object with
 * key + interpolations.
 */
type TrEncodedValue = string | TrEncodedKeyAndInterpolations;

/**
 * Full directive value accepted by `v-tr` / `v-tr.if`.
 * `null`/`undefined` represent "no key available right now".
 */
type TrDirectiveValue = TrEncodedValue | null | undefined;

/**
 * Interpolation map passed to i18next for placeholder substitution.
 */
type TrInterpolations = Record<string, unknown>;

/**
 * Internal normalized representation used by directive update logic.
 */
type TrDirectiveValueNormalized = { key: string, interpolations: TrInterpolations };

/**
 * Vue directive binding type for the `v-tr` directive.
 */
type TrDirectiveBinding = DirectiveBinding<TrDirectiveValue, TrDirectiveModifiers>;

export interface II18NextXHRFormat {
	[culture: string]: {
		[namespace: string]: {
			[key: string]: string
		}
	}
}

// The event data provided by i18next's loaded event.
export interface II18NextLoad {
	[culture: string]: string[]
}

function sanitize(dirty: string): string {
	return DOMPurify.sanitize(dirty, {
		// We allow these attributes on all the below tags. We only
		// need them for <a> tags but they won't hurt on the other ones
		// either and there is no way to configure dompurify to only allow
		// certain combinations.
		ALLOWED_ATTR: ["href", "name", "target", "rel"],
		ALLOWED_TAGS: ["h3", "h4", "h5", "h6", "blockquote", "p", "a",
			"ul", "ol", "nl", "li", "b", "i", "strong", "em", "strike",
			"abbr", "code", "hr", "br", "div", "pre", "#text"],
	});
}

export {
	sanitize,
	LOADING_PLACEHOLDER,
	type TrDirectiveBinding,
	type TrDirectiveModifiers,
	type TrDirectiveValue,
	type TrEncodedKeyAndInterpolations,
	type TrEncodedValue,
};

export default class I18Next {
	/**
	 * Initializes i18next against our TextResource backend.
	 * Prefer calling this from store registration rather than `Vue.use`, since xhr/version are only known then.
	 * @param store Active I18Next Pinia store (`culture`, `xhr`, `version`).
	 * @param embeddedTextResources Optional map of Text Resource keys to resolved strings from page HTML; seeded into i18next so those keys skip XHR.
	 */
	public static async initialize(store: Store, embeddedTextResources: Record<string, string> = {}): Promise<void> {
		if (I18Next.isInstance(I18Next.singleton)) {
			// Page HTML can include a fresh embeddedTextResources map on remount; overwrite in-memory bundles.
			I18Next.seedEmbeddedTRs(store, embeddedTextResources);
			return;
		}

		const singleton = new I18Next(store);

		// We do not yet depend reactively on the version/xhr/client options as we apparently have to specify
		// them upon initialization of i18next. Therefore, changing them in the store does not update texts
		// on the site or reconfigure backends.
		const version = store.version as string;
		const xhr = store.xhr as string;
		const cacheExpirationTimeMs = store.cacheExpirationTimeMs as number;
		const multiloadDebounceMs = store.multiloadDebounceMs as number;

		await i18next
			.use(ChainedBackend)
			.use(I18Next.sanitizer)
			.init({
				// Suppress the Locize sponsor console.info that i18next logs on init.
				showSupportNotice: false,
				backend: {
					backendOptions: [
						{
							// We do explicit versioning but just to be on the safe side we
							// also trash our caches after cacheExpirationTimeMs in case our versioning breaks.
							expirationTime: cacheExpirationTimeMs,
							prefix: "i18next_",
							versions: {
								[MiaPlaza.Control.Vue.I18Next.US]: version,
							},
						},
						{
							backend: XHR,
							backendOption: {
								// eslint-disable-next-line @typescript-eslint/unbound-method
								request: I18Next.request,
								crossDomain: false,
								loadPath: (lngs: string | string[], namespaces: string | string[]) => {
									// i18next-xhr does not properly escape whitespace
									// (it joins all the keys with a '+' without escaping them first.)
									// Even worse, it actually uses its own interpolation logic to replace
									// {{lng}} and {{ns}} in the url so it also escapes things such as ' as &#39; which
									// is not such a brilliant idea in a URL.
									// This is https://github.com/i18next/i18next-xhr-backend/issues/316.
									return xhr.replace("{{lng}}", encodeURIComponent(JSON.stringify(lngs)))
										.replace("{{ns}}", encodeURIComponent(JSON.stringify(namespaces)));
								},
								withCredentials: false,
							} satisfies HttpBackendOptions,
							debounceInterval: multiloadDebounceMs,
						},
					],
					backends: [
						// We lookup strings in the browers local storage first…
						LocalStorageBackend,
						// …and then fallback to loading them from the server in bulk.
						MultiloadAdapter,
					],
				},
				fallbackLng: MiaPlaza.Control.Vue.I18Next.US,
				keySeparator: false,
				load: "currentOnly",
				ns: [],
				// We do not curtail i18next's "nested" feature. However, we do not serve any nested resources yet.
				nsSeparator: ":",
				postProcess: ["html-sanitizer"],
			});

		I18Next.seedEmbeddedTRs(store, embeddedTextResources);
		I18Next.resolveReset(singleton);
	}

	/**
	 * Seeds server-embedded TR values into i18next so hot-path keys resolve without an XHR.
	 */
	private static seedEmbeddedTRs(store: Store, embeddedTextResources: Record<string, string>): void {
		for (const key of Object.keys(embeddedTextResources)) {
			const value = embeddedTextResources[key];
			if (typeof value !== "string") {
				continue;
			}
			i18next.addResourceBundle(
				store.culture,
				I18Next.toNamespace(key),
				{ [MiaPlaza.Control.Vue.I18Next.I18NEXT_KEY]: sanitize(value) },
				true,
				true,
			);
		}
	}

	// Called by Vue.use() to enable the v-tr directive.
	public static install(app: App): void {
		app.directive("tr", {
			// eslint-disable-next-line @typescript-eslint/unbound-method
			mounted: I18Next.beforeMount,
			// eslint-disable-next-line @typescript-eslint/unbound-method
			updated: I18Next.updated,
			// eslint-disable-next-line @typescript-eslint/unbound-method
			unmounted: I18Next.unmounted,
		});

		app.config.globalProperties.$tr = I18Next.$tr;
	}

	// Return a promise that resolves to the value of the TextResource `key`.
	public static tr(key: string, interpolations?: TrInterpolations): Promise<string> {
		return I18Next.isInstance(I18Next.singleton)
			? I18Next.singleton.tr(key, interpolations || {}, true)
			: I18Next.singleton.then((i18n) => i18n.tr(key, interpolations || {}, true));
	}

	// Return the value of the TextResource `key` in a synchronous, reactive way.
	public static resolveTextResource(
		key: string,
		interpolations?: TrInterpolations,
		loading?: string,
	): string {
		return I18Next.$tr(key, interpolations || {}, loading, true);
	}

	// Like resolveTextResource, but does not HTML-escape interpolations.
	// HTML escaping would double-encode apostrophes in names (e.g. Ky'le -> Ka&#39;le).
	public static resolveTextResourceForPlainText(
		key: string,
		interpolations?: TrInterpolations,
		loading?: string,
	): string {
		return I18Next.$tr(key, interpolations || {}, loading, false);
	}

	// Return the value of the TextResource `key`.
	// If the value has not been loaded from the backend yet, make it load in the background and return `loading` instead.
	protected static $tr(key: string, loading?: string): string;
	protected static $tr(key: string, interpolations: TrInterpolations, loading?: string, escapeInterpolations?: boolean): string;
	protected static $tr(key: string, interpolations?: string | TrInterpolations, loading?: string, escapeInterpolations: boolean = true): string {
		if (!key) {
			throw new Error("TextResource key must be a non-empty string.");
		}

		if (typeof interpolations === "string") {
			loading = interpolations;
			interpolations = {};
		}
		loading = loading || LOADING_PLACEHOLDER;

		if (!I18Next.isInstance(I18Next.singleton)) {
			const ready = reactive({ ready: false });
			void I18Next.singleton.then(() => {
				// Render again, when the text resource system has initialized.
				ready.ready = true;
			});
			ready.ready;
			return loading;
		}

		return I18Next.singleton.$tr(key, interpolations || {}, loading, escapeInterpolations);
	}

	// The latest bindings on each element that uses the v-tr directive.
	// This is used to detect outdated delayed calls to the directive hooks.
	private static directiveBindings = new WeakMap<Element, TrDirectiveBinding>();

	// For each element that uses the v-tr directive, a callback that unregisters
	// any watchers that we registered that are not bound to a component's lifetime.
	private static directiveUnwatches = new WeakMap<Element, () => void>();

	// Refresh the inner HTML of this element that uses the v-tr directive.
	// Loads the value of the text resource from the backend if necessary.
	private static async update(el: Element): Promise<void> {
		const setInnerHtml = (html: string) => {
			// Why is it save to set innerHTML to a TextResource value with replacements?
			// * We do not trust our content authors to produce valid HTML, and only allow a certain subset of HTML to go through
			//   (see XHR loader.)
			// * Interpolations (formerly called Replacements) are automatically escaped by the i18next library - unless they are
			//   marked specifically, see -trusted below.
			// * Finally, we apply the sanitizer again to the result of interpolation, so even if somebody managed to inject
			//   something malicious into the interpolation, we take it out if it would not have been allowed in a TextResource
			//   in the first place. E.g., we would take out a <script> block that somebody managed to inject but we would not
			//   take out an <a> pointing to the wrong place.
			if (el.innerHTML !== html) {
				el.innerHTML = html;
			}
		};

		const binding = I18Next.directiveBindings.get(el);
		if (!binding) {
			// The component using the directive has been removed in the meantime.
			return;
		}

		const normalizedBindingValue = I18Next.normalizeDirectiveValue(binding.value);
		if (!normalizedBindingValue && !binding.modifiers.if) {
			// If `v-tr` is used without `.if`, then missing key is a usage error.
			el.textContent = LOADING_PLACEHOLDER;
			throw new Error("v-tr directive requires either a non-empty string key or a key and interpolations object. Use v-tr.if if the key may be null or undefined.");
		}

		if (!normalizedBindingValue) {
			if (!(el instanceof HTMLElement)) {
				// This should never happen, except if `v-tr.if` is used on a non-HTML element.
				throw new Error("v-tr.if modifier can only be used on HTMLElement elements.");
			}

			el.innerHTML = "";
			el.hidden = true;
			return;
		}

		if (binding.modifiers.if && el instanceof HTMLElement) {
			el.hidden = false;
		}

		const { key, interpolations } = normalizedBindingValue;
		const value = I18Next.$tr(key, interpolations, MiaPlaza.Control.Vue.I18Next.I18NEXT_KEY);
		if (value !== MiaPlaza.Control.Vue.I18Next.I18NEXT_KEY) {
			setInnerHtml(value);
		}
		else {
			// The value of this text resource is not available yet so we wait for it.
			el.textContent = LOADING_PLACEHOLDER;

			const value = await I18Next.tr(key, interpolations);
			if (I18Next.directiveBindings.get(el) !== binding) {
				// The bindings have changed since we started to resolve the resource.
				// Ignore this invocation of refresh().
				return;
			}

			setInnerHtml(value);
		}
	}

	// Helper method for the v-tr directive. Sets el.innerHTML to the interpolated text resource specified in the binding,
	// i.e., as the value of v-tr. Also registers a watcher so that the text gets updated whenever the user culture changes.
	protected static beforeMount(el: Element, binding: TrDirectiveBinding): void {
		const store = useI18NextStore();

		// Refresh the rendered text whenever the "culture" changes.
		const unwatch = watch(() => store.culture, () => {
			void I18Next.update(el);
		});

		I18Next.directiveUnwatches.set(el, unwatch);

		// Actually set the content of this element to the value of the text resource.
		I18Next.updated(el, binding);
	};

	/**
	 * Normalizes all supported `v-tr` binding values into one shape.
	 *
	 * Why this returns `null`:
	 * We treat `null` as "no TextResource available right now".
	 * For `v-tr.if`, that means the element can be hidden without logging an error.
	 * For plain `v-tr`, we log an error because a key is required.
	 *
	 * @param bindingValue The value of the `v-tr` directive binding.
	 * @returns Normalized key/interpolations object, or `null` when no key is available.
	 */
	private static normalizeDirectiveValue(bindingValue: TrDirectiveValue): TrDirectiveValueNormalized | null {
		if (!bindingValue) {
			// We return `null` if the value of the binding is `null` or `undefined` or an empty string.
			return null;
		}

		if (typeof bindingValue === "string") {
			return {
				key: bindingValue,
				interpolations: {},
			};
		}

		const { key, ...interpolations } = bindingValue;
		return {
			key: key,
			interpolations: interpolations,
		};
	}

	// Helper method for the v-tr directive. Sets el.innerHTML to the interpolated text resource specified in the binding,
	// i.e., as the value of v-tr.
	protected static updated(el: Element, binding: TrDirectiveBinding): void {
		I18Next.directiveBindings.set(el, binding);
		void I18Next.update(el);
	}

	// Helper method for the v-tr directive. Resets el.innerHTML so Vue.js can
	// safely use this DOM Node elsewhere:
	// Vue.js does not keep track of the innerHTML that has been set by others
	// such as this directive assuming that the node is still empty.
	protected static unmounted(el: Element): void {
		el.innerHTML = "";
		const unwatch = I18Next.directiveUnwatches.get(el);
		if (unwatch == null) {
			void Log.error("unmount hook called for v-tr directive that was never mounted");
			return;
		}
		unwatch();
	}

	// The one I18Next instance, once initialize() has been called or a promise that resolves once I18Next is ready.
	// (Singleton is not a good pattern, but we do not want to go down the road of Dependency Injection with reactivity.)
	private static singleton: I18Next | Promise<I18Next>;
	private static resolveReset: (i18n: I18Next) => void = I18Next.reset();

	// A i18next plugin to make sure that no weird HTML is contained in our Text Resources
	private static readonly sanitizer: PostProcessorModule = {
		name: "html-sanitizer",
		// This sanitizer is only relevant for interpolations which might contain user-provided content.
		// Our text resources are already sanitized by the XHR loader which reports to our logs if there
		// has been illegal HTML.
		process: (value) => sanitize(value),
		type: "postProcessor",
	};

	private static reset() {
		let resolve: ((i18n: I18Next) => void);

		const previousResolve = I18Next.resolveReset;

		// Because of the above singleton pattern, we have to use this really nasty
		// Promise foo, basically abusing promises as synchronization primitives.
		const wrappedResolve = (i18n: I18Next) => {
			resolve(i18n);
			if (previousResolve != null) {
				previousResolve(i18n);
			}
			I18Next.singleton = i18n;
		};
		I18Next.singleton = new Promise((res) => { resolve = res; });
		return wrappedResolve;
	}

	private static isInstance(i18n: I18Next | Promise<I18Next>): i18n is I18Next {
		return (i18n as I18Next).store !== undefined;
	}

	private store: Store;

	private constructor(store: Store) {
		this.store = store;
	}

	private static async request(_options: HttpBackendOptions, url: string, _payload: never, callback: RequestCallback) {
		let response: any;
		try {
			response = await axios.get(url);
		}
		catch (err: any) {
			// See https://github.com/i18next/i18next-xhr-backend/issues/315 for a discussion
			void Log.error(err);
			callback(err, null);
			return;
		}

		let data = response.data;

		// In principle the server is always responding with an II18NextXHRFormat
		// response. However, if for some reason we received a non-error response
		// with invalid data, then i18next silently ignores such errors so we
		// explicitly verify the formatting here.
		function isII18NextXHRFormat(data: any): data is II18NextXHRFormat {
			function isObject(x: any): x is Object {
				return typeof (x) === "object" && x !== null;
			}

			if (!isObject(data)) {
				return false;
			}

			for (const culture in data) {
				const namespaces = data[culture];
				if (!isObject(namespaces)) {
					return false;
				}

				for (const namespace in namespaces) {
					const values = namespaces[namespace];
					if (!isObject(values)) {
						return false;
					}

					for (const key in values) {
						if (key !== MiaPlaza.Control.Vue.I18Next.I18NEXT_KEY) {
							return false;
						}

						const value = values[key];
						if (typeof (value) !== "string") {
							return false;
						}
					}
				}
			}

			return true;
		}

		if (!isII18NextXHRFormat(data)) {
			const msg = `Server response ${JSON.stringify(data)} not conforming to i18next format`;
			void Log.error(msg);

			// The 400 code "Bad Request" is somewhat random. It does not seem to matter to the underlying implementation.
			callback(new Error(msg), null);
			return;
		}

		data = mapValues(data, (culture) =>
			mapValues(culture, (ns, key) =>
				mapValues(ns, (value) => {
					const sanitized
						= I18Next.sanitizer.process(value, undefined as never, undefined as never, undefined as never);
					if (sanitized !== value) {
						const message = `\`${key}\` had unsupported HTML. \`${value}\` was normalized into \`${sanitized}\`.`;
						void Log.error(message);
					}
					return sanitized;
				})));

		callback(null, { data, status: response.status });
	}

	private $tr(key: string, interpolations: TrInterpolations, loading: string, escapeInterpolations: boolean = true): string {
		const reactiveTR = reactive({ value: this.fetch(key, interpolations, this.store.culture, escapeInterpolations) });
		if (reactiveTR.value === MiaPlaza.Control.Vue.I18Next.I18NEXT_KEY) {
			void this.tr(key, interpolations, escapeInterpolations).then((value) => {
				if (value !== reactiveTR.value) {
					reactiveTR.value = value;
				}
			});
			return loading;
		}
		else {
			return reactiveTR.value;
		}
	}

	private tr(key: string, interpolations: TrInterpolations, escapeInterpolations: boolean = true): Promise<string> {
		const culture = this.store.culture;
		const value = this.fetch(key, interpolations, culture, escapeInterpolations);
		return new Promise((resolve) => {
			if (value === MiaPlaza.Control.Vue.I18Next.I18NEXT_KEY) {
				// Load the text from the server. We do not load them one by one but bulk load
				// with the multiload adapter.
				// We load each TextResource as a separate namespace into i18next and cache it in the local storage
				// at the client. We need the namespacing hack because i18next uses namespaces as the atomic unit
				// that can be loaded at a time.

				if (culture !== i18next.language) {
					// Change i18next language so we do not load namespaces for the wrong language
					const resolveReset = I18Next.reset();
					void i18next.changeLanguage(culture).then(() => resolveReset(this));
				}
				if (!I18Next.isInstance(I18Next.singleton)) {
					// I18Next is in the process of resetting. Postpone querying the server until it is done.
					void I18Next.singleton.then((i18n) => i18n.tr(key, interpolations, escapeInterpolations).then(resolve));
					return;
				}
				void i18next.loadNamespaces([I18Next.toNamespace(key)]).then(() => {
					const newValue = this.fetch(key, interpolations, culture, escapeInterpolations);
					resolve(newValue);
					if (newValue === MiaPlaza.Control.Vue.I18Next.I18NEXT_KEY) {
						throw new Error(`Failed to load resource for \`${key}\` with interpolations ${JSON.stringify(interpolations)}.`);
					}
				});
			}
			else {
				return resolve(value);
			}
		});
	}

	private fetch(key: string, interpolations: TrInterpolations, lng: string, escapeInterpolations: boolean = true): string {
		if (lng === MiaPlaza.Control.Vue.I18Next.C) {
			const prettyInterpolations = JSON.stringify(interpolations);
			if (prettyInterpolations === "[]") {
				return `[${key}]`;
			}
			return `[${key}](with ${prettyInterpolations})`;
		}

		return i18next.t(MiaPlaza.Control.Vue.I18Next.I18NEXT_KEY, {
			...(interpolations || {}),
			fallbackLng: lng,
			// strangely, loading does not work anymore if we put this into the global init() options.
			interpolation: {
				prefix: "{",
				suffix: "}",
				unescapePrefix: "-(?=trusted)",
				escapeValue: escapeInterpolations,
			},
			lng,
			ns: I18Next.toNamespace(key),
		});
	}

	private static toNamespace(key: string) {
		// Model.TextResources says that a key must not contain whitespace.
		return key.replace(/ /g, "_");
	}
}
